import { useEffect, useRef, useState } from 'react';
import { Modal } from '@mui/material';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';

import { getPblCase, respondPblCaseAssignment } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import { Button } from '@/components/UI/Button';
import useResize from '@/hooks/useResize';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';
import PATH from '@/router/path';
import type { PblCase, PblDocument } from '@/types/pbl';
import { translatePblDisplayList, translatePblDisplayValue } from '@/utils/pblDisplayTranslation';
import { errorHandler, successHandler } from '@/utils/toastHandler';

import { mapPblCaseToOcPendingReviewCase } from '../OcManageCases/caseReviewState';
import {
  ActionRow,
  DetailSectionPanel,
  InfoGrid,
  InfoLabel,
  InfoValue,
  MatchNoticeBox,
  ModalAction,
  ModalSurface,
  ModalText,
  ModalTitle,
  SectionTitle,
  TitleBar,
} from '../OcManageCases/styles';
import type { PblOcPendingReviewCase } from '../OcManageCases/types';
import { buildLawyerAssignmentResponsePayload, findCurrentPendingLawyerAssignment } from './lawyerCaseState';
import { openStorageDownload } from '@/utils/storageDownload';
import { useTranslation } from 'react-i18next';

const EnquiryPanel = styled.section`
  box-sizing: border-box;
  margin: 28px 0;
  padding: 22px 18px;
  border: 1px solid ${(p) => p.theme.colors.gray300};
  border-radius: ${(p) => p.theme.radii.base};

  @media (max-width: 639px) {
    margin: 20px 0;
    padding: 18px 12px;
  }
`;

const EnquiryGrid = styled(InfoGrid)`
  grid-template-columns: 300px minmax(0, 1fr);
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 121px minmax(0, 1fr);
    gap: 8px 12px;
  }
`;

const Divider = styled.hr`
  margin: 16px 0;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.colors.gray100};
`;

const DescriptionText = styled.p`
  margin: 0;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.5;
`;

const SupportingDocumentLink = styled.a`
  color: ${(p) => p.theme.colors.primary300};
  font-weight: 700;
  text-decoration: underline;
`;

const DeclineTextarea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  min-height: 112px;
  margin-top: 24px;
  padding: 10px 12px;
  border: 1px solid ${(p) => p.theme.colors.gray100};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.light};
  color: ${(p) => p.theme.colors.gray500};
  font-family: inherit;
  font-size: ${(p) => p.theme.fontSizes.base};
  resize: vertical;
`;

const buildLawyerMeetingCalendarPath = (caseId: string | number) =>
  PATH.pbl.lawyerMeetingCalendar.replace(':caseId', String(caseId));

const getReviewDocumentFileName = (document: PblDocument) =>
  document.storage_file?.name || document.title || '-';

const CaseDocumentsPanel = ({ documents }: { documents: PblDocument[] }) => {
  //   @ts-ignore
  const { t } = useTranslation();
  return (
  <EnquiryPanel>
    <SectionTitle>{t('pbl.pagesHardcoded.lawyerManageCases.detail.documentSUpload')}</SectionTitle>
    {documents.length > 0 ? (
      documents.map((document) => (
        <EnquiryGrid key={document.id}>
          <InfoLabel>{t('pbl.common.labels.title', { defaultValue: 'Title' })}</InfoLabel>
          <InfoValue>{document.title || '-'}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.documentType')}</InfoLabel>
          <InfoValue>{document.document_type || '-'}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.file')}</InfoLabel>
          <InfoValue>
            {document.storage_file?.path ? (
              <SupportingDocumentLink href="#" onClick={(event) => { event.preventDefault(); void openStorageDownload(document.storage_file!.path); }}>
                {getReviewDocumentFileName(document)}
              </SupportingDocumentLink>
            ) : (
              getReviewDocumentFileName(document)
            )}
          </InfoValue>
        </EnquiryGrid>
      ))
    ) : (
      <DescriptionText>{t('pbl.pagesHardcoded.lawyerManageCases.detail.noDocumentsUploaded')}</DescriptionText>
    )}
  </EnquiryPanel>
);
};

const PblLawyerPendingReviewDetail = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const { isMobile, isTablet } = useResize();
  const { userData } = useAppSelector(getAuthSelector);
  const [item, setItem] = useState<PblOcPendingReviewCase | null>(null);
  const [backendCase, setBackendCase] = useState<PblCase | null>(null);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [isResponding, setIsResponding] = useState(false);
  const isRespondingRef = useRef(false);

  const pendingAssignment = findCurrentPendingLawyerAssignment(backendCase, userData);
  const canRespond = Boolean(pendingAssignment);

  const loadCase = async (isMounted = true) => {
    const numericCaseId = Number(caseId);

    if (!Number.isFinite(numericCaseId)) {
      errorHandler(t('pbl.pagesHardcoded.lawyerManageCases.detail.invalidCaseId'));
      return;
    }

    try {
      const response = await getPblCase(numericCaseId);

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.lawyerManageCases.detail.failedToLoadCase'));
        return;
      }

      if (isMounted) {
        setBackendCase(response.data.data);
        setItem(mapPblCaseToOcPendingReviewCase(response.data.data));
      }
    } catch (error: any) {
      if (isMounted) {
        errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.lawyerManageCases.detail.failedToLoadCase'));
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    loadCase();

    return () => {
      isMounted = false;
    };
  }, [caseId]);

  const submitAssignmentResponse = async (decision: 'accept' | 'decline') => {
    if (!item || !pendingAssignment || isRespondingRef.current) return;

    if (decision === 'decline' && !declineReason.trim()) {
      errorHandler(t('pbl.pagesHardcoded.lawyerManageCases.detail.pleaseEnterADeclinedReason'));
      return;
    }

    isRespondingRef.current = true;
    setIsResponding(true);

    try {
      const response = await respondPblCaseAssignment(
        Number(item.id),
        pendingAssignment.id,
        buildLawyerAssignmentResponsePayload(decision, declineReason)
      );

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.lawyerManageCases.detail.failedToSubmitResponse'));
        return;
      }

      successHandler(response.data.message || t('pbl.pagesHardcoded.lawyerManageCases.detail.caseResponseSubmitted'));
      setAcceptOpen(false);
      setDeclineOpen(false);
      setDeclineReason('');
      if (decision === 'accept') {
        navigate(buildLawyerMeetingCalendarPath(item.id));
        return;
      }
      navigate(PATH.pbl.lawyerPendingReviewCases);
      return;
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.lawyerManageCases.detail.failedToSubmitResponse'));
    } finally {
      isRespondingRef.current = false;
      setIsResponding(false);
    }
  };

  if (!item) {
    return (
      <PageContainer
        title={
          <TitleBar isMobile={isMobile}>
            <Button
              type="button"
              size="md"
              variant="primary"
              rounded={false}
              onClick={() => navigate(PATH.pbl.lawyerPendingReviewCases)}
            >
              {t('pbl.pagesHardcoded.lawyerManageCases.detail.back')}</Button>
            <span>
              {t('pbl.pagesHardcoded.lawyerManageCases.detail.home')}<strong>{t('pbl.pagesHardcoded.lawyerManageCases.detail.proBonoLegalServiceCasesReview')}</strong>
            </span>
          </TitleBar>
        }
      >
        <DetailSectionPanel isTablet={isTablet}>
          <DescriptionText>{t('pbl.pagesHardcoded.lawyerManageCases.detail.loadingCase')}</DescriptionText>
        </DetailSectionPanel>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button
            type="button"
            size="md"
            variant="primary"
            rounded={false}
            onClick={() => navigate(PATH.pbl.lawyerPendingReviewCases)}
          >
            {t('pbl.pagesHardcoded.lawyerManageCases.detail.back')}</Button>
          <span>
            {t('pbl.pagesHardcoded.lawyerManageCases.detail.home')}<strong>{t('pbl.pagesHardcoded.lawyerManageCases.detail.proBonoLegalServiceCasesReview')}</strong>
          </span>
        </TitleBar>
      }
    >
      <DetailSectionPanel isTablet={isTablet}>
        <SectionTitle>{t('pbl.pagesHardcoded.lawyerManageCases.detail.districtOfficeStaffInformation')}</SectionTitle>
        <InfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.legcoMembersOffice')}</InfoLabel>
          <InfoValue>{item.officeName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.assistingStaffNameLegcoMembersOffice')}</InfoLabel>
          <InfoValue>{item.assistingStaffName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.officeContact')}</InfoLabel>
          <InfoValue>{item.officeContact}</InfoValue>
        </InfoGrid>

        <SectionTitle>{t('pbl.pagesHardcoded.lawyerManageCases.detail.caseInformation')}</SectionTitle>
        <InfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.caseId')}</InfoLabel>
          <InfoValue>{translatePblDisplayValue(t, item.caseId)}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.enquirer')}</InfoLabel>
          <InfoValue>{item.enquirer.fullName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.hkid4Digits')}</InfoLabel>
          <InfoValue>{item.enquirer.identityPartial}</InfoValue>
        </InfoGrid>

        <MatchNoticeBox>
          {t('pbl.pagesHardcoded.lawyerManageCases.detail.aProBonoConsultationCaseIsAssignedTo')}<br />
          {t('pbl.pagesHardcoded.lawyerManageCases.detail.theMatchingResultIsBasedOn')}<br />
          {t('pbl.pagesHardcoded.lawyerManageCases.detail.caseRelevance')}<br />
          {t('pbl.pagesHardcoded.lawyerManageCases.detail.theDutyRecordsOfTheProBonoLawyer')}<br />
          <br />
          {t('pbl.pagesHardcoded.lawyerManageCases.detail.finalConfirmationRemainsWithTheLegcoMemberS')}</MatchNoticeBox>

        <EnquiryPanel>
          <SectionTitle>{t('pbl.pagesHardcoded.lawyerManageCases.detail.proBonoEnquiryInformation')}</SectionTitle>
          <EnquiryGrid>
            <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.enquirer')}</InfoLabel>
            <InfoValue>{item.enquirer.fullName}</InfoValue>
            <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.enquiryCategory')}</InfoLabel>
            <InfoValue>{translatePblDisplayValue(t, item.enquiryCategory)}</InfoValue>
            {(backendCase?.urgency || backendCase?.court_deadline) && (
              <>
                <InfoLabel>{t('pbl.citizenAuthorizationCaseCreate.caseApplication.urgency.level')}</InfoLabel>
                <InfoValue>
                  {backendCase?.urgency
                    ? t(`pbl.citizenAuthorizationCaseCreate.caseApplication.urgency.${backendCase.urgency}`)
                    : '-'}
                </InfoValue>
                <InfoLabel>{t('pbl.citizenAuthorizationCaseCreate.caseApplication.urgency.courtDeadline')}</InfoLabel>
                <InfoValue>{backendCase?.court_deadline || '-'}</InfoValue>
              </>
            )}
            <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.specificQueries')}</InfoLabel>
            <InfoValue>{item.specificQueries}</InfoValue>
            <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.enquiryRequirementsExpectations')}</InfoLabel>
            <InfoValue>{translatePblDisplayList(t, item.primaryLegalServiceNeeds)}</InfoValue>
            <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.preferredConsultationMethod')}</InfoLabel>
            <InfoValue>{translatePblDisplayList(t, item.preferredConsultationMethods)}</InfoValue>
          </EnquiryGrid>
          <Divider />
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerManageCases.detail.detailedDescriptionOfLegalIssue')}</InfoLabel>
          <DescriptionText>{item.factualBackground}</DescriptionText>
        </EnquiryPanel>

        <CaseDocumentsPanel documents={backendCase?.documents || []} />

        {canRespond ? (
          <ActionRow isMobile={isMobile}>
            <Button type="button" size="lg" variant="regular" rounded={false} disabled={isResponding} onClick={() => setAcceptOpen(true)}>
              {t('pbl.pagesHardcoded.lawyerManageCases.detail.accept')}</Button>
            <Button
              type="button"
              size="lg"
              variant="primary-invert"
              rounded={false}
              disabled={isResponding}
              onClick={() => setDeclineOpen(true)}
            >
              {t('pbl.pagesHardcoded.lawyerManageCases.detail.declined')}</Button>
          </ActionRow>
        ) : (
          <DescriptionText></DescriptionText>
        )}
      </DetailSectionPanel>

      <Modal
        open={acceptOpen}
        onClose={() => {
          if (!isRespondingRef.current) setAcceptOpen(false);
        }}
        aria-labelledby="lawyer-accept-case-title"
      >
        <ModalSurface>
          <ModalTitle id="lawyer-accept-case-title">{t('pbl.pagesHardcoded.lawyerManageCases.detail.youAreGoingToAcceptTheProBono')}</ModalTitle>
          <ModalText>
            {t('pbl.pagesHardcoded.lawyerManageCases.detail.byAcceptingYouArePromisedToOfferA')}</ModalText>
          <ModalText>{t('pbl.pagesHardcoded.lawyerManageCases.detail.pleaseEnsureThatAllInformationProvidedIsAccurate')}</ModalText>
          <ModalAction>
            <Button
              type="button"
              size="lg"
              variant="primary"
              rounded={false}
              disabled={isResponding}
              onClick={() => submitAssignmentResponse('accept')}
            >
              {isResponding ? t('pbl.pagesHardcoded.lawyerManageCases.detail.submitting') : t('pbl.pagesHardcoded.lawyerManageCases.detail.acceptAndScheduleTheMeeting')}
            </Button>
            <Button
              type="button"
              size="lg"
              variant="primary-invert"
              rounded={false}
              disabled={isResponding}
              onClick={() => setAcceptOpen(false)}
            >
              {t('pbl.pagesHardcoded.lawyerManageCases.detail.back')}</Button>
          </ModalAction>
        </ModalSurface>
      </Modal>

      <Modal
        open={declineOpen}
        onClose={() => {
          if (!isRespondingRef.current) setDeclineOpen(false);
        }}
        aria-labelledby="lawyer-decline-case-title"
      >
        <ModalSurface>
          <ModalTitle id="lawyer-decline-case-title">{t('pbl.pagesHardcoded.lawyerManageCases.detail.declinedReasons')}</ModalTitle>
          <ModalText>{t('pbl.pagesHardcoded.lawyerManageCases.detail.pleaseEnterTheReasonYouDeclinedThisCase')}</ModalText>
          <DeclineTextarea
            aria-label={t('pbl.pagesHardcoded.lawyerManageCases.detail.declinedReason')}
            placeholder={t('pbl.pagesHardcoded.lawyerManageCases.detail.declinedReason2')}
            value={declineReason}
            disabled={isResponding}
            onChange={(event) => setDeclineReason(event.target.value)}
          />
          <ModalAction>
            <Button
              type="button"
              size="lg"
              variant="primary"
              rounded={false}
              disabled={isResponding}
              onClick={() => submitAssignmentResponse('decline')}
            >
              {isResponding
                ? t('pbl.pagesHardcoded.lawyerManageCases.detail.submitting')
                : t('pbl.common.actions.ok', { defaultValue: 'OK' })}
            </Button>
            <Button
              type="button"
              size="lg"
              variant="primary-invert"
              rounded={false}
              disabled={isResponding}
              onClick={() => setDeclineOpen(false)}
            >
              {t('pbl.pagesHardcoded.lawyerManageCases.detail.cancel')}</Button>
          </ModalAction>
        </ModalSurface>
      </Modal>
    </PageContainer>
  );
};

export default PblLawyerPendingReviewDetail;
